import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Form } from 'react-final-form'
import { Row, Col, Button, Spin, Upload, Image, Typography, Divider, message, Modal } from 'antd'
import { Field } from 'react-final-form'
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons'
import moment from 'moment'
import TextInput from 'components/TextInput'
import TextArea from 'components/TextArea/TextArea'
import Select from 'components/Select'
import DatePicker from 'components/DatePicker/DatePicker'
import RuleSearchSelect from 'components/RuleSearchSelect'
import countries from 'const/country'
import useAction from 'utils/useAction'
import { selectors as arcAdsSelectors } from 'domains/arcAds'
import { selectors as arcCategoriesSelectors } from 'domains/arcCategories'
import { selectors as arcCampaignsSelectors } from 'domains/arcCampaigns'
import * as arcAdsActions from 'actions/views/arcAds'
import * as arcCategoriesActions from 'actions/views/arcCategories'
import * as arcCampaignsActions from 'actions/views/arcCampaigns'
import { formatArcAdForApi, formatArcAdForForm, STATUS_OPTIONS } from 'domains/arcAds/formatters'
import { PLATFORM_OPTIONS, PLATFORMS, IMAGE_CONFIG, ALLOWED_IMAGE_TYPES } from 'domains/arcAds/const'
import { StyledForm, FormSection, ButtonContainer, ImageUploadSection, ImagePreview, FullWidthFieldWrapper } from './AdForm.styles'
import LinkConverter from './LinkConverter'

const { Title, Text } = Typography

// Custom Select component that only shows errors when touched
const TouchAwareSelect = (props) => {
  const { meta, input, ...rest } = props
  // Only show error if field has been touched AND there's actually an error
  const hasValue = input.value?.value || input.value
  const modifiedMeta = {
    ...meta,
    error: meta.touched && meta.error && !hasValue ? meta.error : undefined
  }
  return <Select {...rest} meta={modifiedMeta} input={input} />
}

const validateRequired = (value) => {
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return 'This field is required'
  }
  return undefined
}

const validateOptionalURL = (value) => {
  if (!value || value.trim() === '') return undefined
  const urlPattern = /^https?:\/\/.+\..+/
  if (!urlPattern.test(value)) {
    return 'Please enter a valid URL'
  }
  return undefined
}

const validateRequiredURL = (value) => {
  const requiredError = validateRequired(value)
  if (requiredError) return requiredError
  return validateOptionalURL(value)
}

// Countries options for select
const countryOptions = countries.map(country => ({
  value: country.code,
  label: `${country.code} - ${country.name}`
}))

const AdForm = ({ adId, campaignId, onSuccess, onCancel }) => {
  const [iconFile, setIconFile] = useState(null)
  const [heroFile, setHeroFile] = useState(null)
  const [logoIconFile, setLogoIconFile] = useState(null)
  const [iconPreview, setIconPreview] = useState(null)
  const [heroPreview, setHeroPreview] = useState(null)
  const [logoIconPreview, setLogoIconPreview] = useState(null)
  const [isUploadingIcon, setIsUploadingIcon] = useState(false)
  const [isUploadingHero, setIsUploadingHero] = useState(false)
  const [isUploadingLogoIcon, setIsUploadingLogoIcon] = useState(false)
  const [removedImages, setRemovedImages] = useState({
    icon: false,
    hero: false,
    logoIcon: false
  })

  const isSaving = useSelector(arcAdsSelectors.getIsSavingActive)
  const selectedAd = useSelector(arcAdsSelectors.getSelectedAd)
  const getAdById = useSelector(arcAdsSelectors.getArcAdById)
  const includedData = useSelector(arcAdsSelectors.getIncludedData)
  const sources = useSelector(arcAdsSelectors.getSources)
  const isSourcesLoaded = useSelector(arcAdsSelectors.getIsSourcesLoaded)
  const categories = useSelector(arcCategoriesSelectors.getArcCategories)
  const isCategoriesLoaded = useSelector(arcCategoriesSelectors.getIsLoaded)
  const campaigns = useSelector(arcCampaignsSelectors.getArcCampaigns)
  const isCampaignsLoaded = useSelector(arcCampaignsSelectors.getIsLoaded)
  const getCampaignById = useSelector(arcCampaignsSelectors.getCampaignById)
  const createAd = useAction(arcAdsActions.createArcAd)
  const updateAd = useAction(arcAdsActions.updateArcAd)
  const fetchAdDetails = useAction(arcAdsActions.fetchArcAdDetails)
  const fetchSources = useAction(arcAdsActions.fetchArcAdSources)
  const fetchCategories = useAction(arcCategoriesActions.fetchArcCategories)
  const fetchCampaigns = useAction(arcCampaignsActions.fetchArcCampaigns)

  const ad = adId ? getAdById(adId) || selectedAd : null
  const isEdit = !!adId


  useEffect(() => {
    if (!isSourcesLoaded) {
      fetchSources()
    }
  }, [isSourcesLoaded, fetchSources])

  useEffect(() => {
    if (!isCategoriesLoaded) {
      fetchCategories()
    }
  }, [isCategoriesLoaded, fetchCategories])

  useEffect(() => {
    if (!isCampaignsLoaded) {
      fetchCampaigns({ page: 1, search: '', filters: {}, sorter: {} })
    }
  }, [isCampaignsLoaded, fetchCampaigns])

  useEffect(() => {
    if (adId && !ad) {
      fetchAdDetails({ id: adId })
    }
  }, [adId, ad, fetchAdDetails])

  // Set initial image previews when ad data is loaded
  useEffect(() => {
    if (isEdit && ad && ad.attributes) {
      setIconPreview(ad.attributes.icon_url || null)
      setHeroPreview(ad.attributes.hero_url || null)
      setLogoIconPreview(ad.attributes.logo_icon_url || null)
      setIconFile(null)
      setHeroFile(null)
      setLogoIconFile(null)
      setRemovedImages({
        icon: false,
        hero: false,
        logoIcon: false
      })
    }
  }, [isEdit, ad])

  const convertFileToBase64 = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = error => reject(error)
    })
  }, [])

  const createUploadHandler = useCallback((imageType) => {
    const config = IMAGE_CONFIG[imageType]
    if (!config) {
      throw new Error(`Invalid image type: ${imageType}`)
    }

    return async (file) => {
      try {
        // Validate file size
        if (file.size > config.maxSize) {
          message.error(`File size must be less than ${Math.round(config.maxSize / (1024 * 1024))}MB`)
          return false
        }

        // Validate file type
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
          message.error(`Please upload a valid image file (${ALLOWED_IMAGE_TYPES.map(type => type.split('/')[1].toUpperCase()).join(', ')})`)
          return false
        }

        // Set uploading state
        if (imageType === 'icon') setIsUploadingIcon(true)
        else if (imageType === 'hero') setIsUploadingHero(true)
        else if (imageType === 'logoIcon') setIsUploadingLogoIcon(true)

        const base64 = await convertFileToBase64(file)

        // Set file and preview states
        if (imageType === 'icon') {
          setIconFile(file)
          setIconPreview(base64)
          setRemovedImages(prev => ({ ...prev, icon: false }))
        } else if (imageType === 'hero') {
          setHeroFile(file)
          setHeroPreview(base64)
          setRemovedImages(prev => ({ ...prev, hero: false }))
        } else if (imageType === 'logoIcon') {
          setLogoIconFile(file)
          setLogoIconPreview(base64)
          setRemovedImages(prev => ({ ...prev, logoIcon: false }))
        }

        message.success(`${config.name} uploaded successfully`)
        return false // Prevent auto upload
      } catch (error) {
        console.error(`Error converting ${config.name.toLowerCase()} to base64:`, error)
        message.error(`Failed to upload ${config.name.toLowerCase()}. Please try again.`)
        return false
      } finally {
        // Reset uploading state
        if (imageType === 'icon') setIsUploadingIcon(false)
        else if (imageType === 'hero') setIsUploadingHero(false)
        else if (imageType === 'logoIcon') setIsUploadingLogoIcon(false)
      }
    }
  }, [convertFileToBase64])

  const createRemoveHandler = useCallback((imageType) => {
    const config = IMAGE_CONFIG[imageType]
    if (!config) {
      throw new Error(`Invalid image type: ${imageType}`)
    }

    return () => {
      Modal.confirm({
        title: `Remove ${config.name}`,
        content: `Are you sure you want to remove the current ${config.name.toLowerCase()}? The image will be removed when you save the form.`,
        okText: 'Yes, Remove',
        cancelText: 'Cancel',
        okType: 'danger',
        onOk() {
          if (imageType === 'icon') {
            setIconFile(null)
            setIconPreview(null)
            setRemovedImages(prev => ({ ...prev, icon: true }))
          } else if (imageType === 'hero') {
            setHeroFile(null)
            setHeroPreview(null)
            setRemovedImages(prev => ({ ...prev, hero: true }))
          } else if (imageType === 'logoIcon') {
            setLogoIconFile(null)
            setLogoIconPreview(null)
            setRemovedImages(prev => ({ ...prev, logoIcon: true }))
          }
          message.info(`${config.name} marked for removal. Save the form to apply changes.`)
        },
      })
    }
  }, [])

  // Create upload handlers using the factory
  const handleIconUpload = useCallback(createUploadHandler('icon'), [createUploadHandler])
  const handleHeroUpload = useCallback(createUploadHandler('hero'), [createUploadHandler])
  const handleLogoIconUpload = useCallback(createUploadHandler('logoIcon'), [createUploadHandler])

  // Create remove handlers using the factory
  const handleRemoveIcon = useCallback(createRemoveHandler('icon'), [createRemoveHandler])
  const handleRemoveHero = useCallback(createRemoveHandler('hero'), [createRemoveHandler])
  const handleRemoveLogoIcon = useCallback(createRemoveHandler('logoIcon'), [createRemoveHandler])

  const handleSubmit = async (values) => {
    try {
      if (values.categories && Array.isArray(values.categories)) {
        values.categories = values.categories.map(categoryValue => {
          const catVal = categoryValue.value || categoryValue.label || categoryValue
          const foundCategory = categories.find(cat => cat.attributes.name === catVal)
          return foundCategory ? foundCategory.id : categoryValue.key
        })
      }

      const formattedData = formatArcAdForApi(values)

      if (removedImages.icon && !iconFile) {
        formattedData.attributes.clear_icon_image = true
      }
      if (removedImages.hero && !heroFile) {
        formattedData.attributes.clear_hero_image = true
      }
      if (removedImages.logoIcon && !logoIconFile) {
        formattedData.attributes.clear_logo_icon_image = true
      }
      if (iconFile && iconPreview && iconPreview.startsWith('data:')) {
        formattedData.attributes.icon_url_base64 = iconPreview
        if (!formattedData.attributes.icon_width) {
          formattedData.attributes.icon_width = IMAGE_CONFIG.icon.defaultDimensions.width
        }
        if (!formattedData.attributes.icon_height) {
          formattedData.attributes.icon_height = IMAGE_CONFIG.icon.defaultDimensions.height
        }
      }

      if (heroFile && heroPreview && heroPreview.startsWith('data:')) {
        formattedData.attributes.hero_url_base64 = heroPreview
        if (!formattedData.attributes.hero_width) {
          formattedData.attributes.hero_width = IMAGE_CONFIG.hero.defaultDimensions.width
        }
        if (!formattedData.attributes.hero_height) {
          formattedData.attributes.hero_height = IMAGE_CONFIG.hero.defaultDimensions.height
        }
      }

      if (logoIconFile && logoIconPreview && logoIconPreview.startsWith('data:')) {
        formattedData.attributes.logo_icon_url_base64 = logoIconPreview
        if (!formattedData.attributes.logo_icon_width) {
          formattedData.attributes.logo_icon_width = IMAGE_CONFIG.logoIcon.defaultDimensions.width
        }
        if (!formattedData.attributes.logo_icon_height) {
          formattedData.attributes.logo_icon_height = IMAGE_CONFIG.logoIcon.defaultDimensions.height
        }
      }

      if (isEdit) {
        formattedData.id = adId
        updateAd({
          data: formattedData,
          callback: () => {
            fetchAdDetails({ id: adId })
            onSuccess()
          }
        })
      } else {
        createAd({
          data: formattedData,
          callback: () => {
            onSuccess()
          }
        })
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      message.error('Failed to save ad. Please try again.')
    }
  }


  const categoryOptions = (categories || []).map(category => ({
    value: category.id,
    label: category.attributes.name
  }))

  const campaignOptions = (campaigns || []).map(campaign => ({
    value: campaign.id,
    label: campaign.attributes?.name || `Campaign ${campaign.id}`
  }))

  const selectedCampaign = campaignId ? getCampaignById(campaignId) : null
  const campaignSource = selectedCampaign?.attributes?.source
  const getSourceIdFromCampaignSource = (source) => {
    if (!source) return null
    const matchingSource = sources.find(s =>
      s.name?.toLowerCase() === source.toLowerCase()
    )
    return matchingSource ? matchingSource.id : null
  }

  const initialValues = isEdit && ad
    ? formatArcAdForForm(ad, includedData)
    : campaignId
      ? {
          campaign_id: campaignId,
          source_id: getSourceIdFromCampaignSource(campaignSource)
        }
      : {}

  const isKarmaSource = (values) => {
    // Handle both create mode (object with value property) and edit mode (raw value)
    if (!values || values.source_id === undefined || values.source_id === null) {
      return false
    }
    const sourceValue = values.source_id?.value !== undefined ? values.source_id.value : values.source_id
    return sourceValue === 0 || (sourceValue && typeof sourceValue === 'string' && sourceValue.toLowerCase() === 'karma')
  }

  const isMobilePlatform = (values) => {
    // Handle both create mode (object with value property) and edit mode (raw value)
    if (!values || values.platform === undefined || values.platform === null) {
      return false
    }
    const platformValue = values.platform?.value !== undefined ? values.platform.value : values.platform
    return platformValue === PLATFORMS.MOBILE || (platformValue && typeof platformValue === 'string' && platformValue.toLowerCase() === 'mobile')
  }

  if (isEdit && !ad) {
    return (
      <Spin spinning={true}>
        <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Loading ad details...
        </div>
      </Spin>
    )
  }

  // Show loading state if sources or categories are not loaded yet
  if (!isSourcesLoaded || !isCategoriesLoaded) {
    return (
      <Spin spinning={true}>
        <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Loading form data...
        </div>
      </Spin>
    )
  }

  return (
    <Spin spinning={isSaving}>
      <Form
        key={isEdit ? `${adId}-${ad?.id}-${ad?.attributes?.icon_url}-${ad?.attributes?.hero_url}-${ad?.attributes?.logo_icon_url}` : 'create'} // Force re-render when ad data or images change
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validate={(values) => {
          const errors = {}
          // Manual validation to ensure required fields are checked
          if (!values.campaign_id) {
            errors.campaign_id = 'Campaign ID is required'
          }
          if (!values.header) {
            errors.header = 'Header is required'
          }
          if(!values.cta_text) {
            errors.cta_text = 'Call-to-Action Text is required'
          }
          const isKarma = isKarmaSource(values)
          if(!values.secondary_cta_text && isKarma) {
            errors.secondary_cta_text = 'Secondary Call-to-Action Text is required'
          }
          if(!values.third_cta_text && isKarma && isMobilePlatform(values)) {
            errors.third_cta_text = 'Third Call-to-Action Text is required'
          }
          if (!values.click_url) {
            errors.click_url = 'Click URL is required'
          }
          if (!values.rule_id && isKarma) {
            errors.rule_id = 'Rule ID is required'
          }
          if (!campaignId && !isEdit) {
            const sourceValue = values.source_id?.value || values.source_id
            if (!sourceValue) {
              errors.source_id = 'Source is required'
            }
          }
          // Check if platform is properly selected
          const platformValue = values.platform?.value || values.platform
          if (!platformValue) {
            errors.platform = 'Platform is required'
          }
          return errors
        }}
        render={({ handleSubmit, pristine, hasValidationErrors, values, form }) => {
          return (
            <StyledForm onSubmit={handleSubmit}>
              <FormSection>
                <Title level={4}>Basic Information</Title>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Banner ID
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Unique identifier for the banner campaign
                    </Text>
                    <Field
                      name="banner_id"
                      label="Banner ID"
                      placeholder="Enter banner ID"
                      component={TextInput}
                    />
                  </Col>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Campaign *
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      {isEdit ? 'Associated campaign (cannot be changed)' : 'Select associated campaign'}
                    </Text>
                    {isEdit ? (
                      <>
                        {/* Non-editable campaign display for edit mode */}
                        {(() => {
                          const currentCampaignId = initialValues.campaign_id || campaignId
                          const currentCampaign = currentCampaignId ? getCampaignById(currentCampaignId) : null
                          const displayText = currentCampaign
                            ? `${currentCampaign.attributes?.name || `Campaign ${currentCampaign.id}`} (ID: ${currentCampaign.id})`
                            : `Campaign ID: ${currentCampaignId}`
                          return (
                            <div style={{
                              padding: '8px 12px',
                              border: '1px solid #d9d9d9',
                              borderRadius: '4px',
                              backgroundColor: '#f5f5f5',
                              color: '#666'
                            }}>
                              {displayText}
                            </div>
                          )
                        })()}
                        {/* Hidden field to preserve campaign_id value */}
                        <Field
                          name="campaign_id"
                          type="hidden"
                          component="input"
                        />
                      </>
                    ) : (
                      <>
                        {selectedCampaign && (
                          <Text style={{ display: 'block', marginBottom: 8, color: '#1890ff' }}>
                            Selected: {selectedCampaign.attributes?.name || `Campaign ${selectedCampaign.id}`}
                          </Text>
                        )}
                        <Field
                          required
                          name="campaign_id"
                          label="Campaign"
                          placeholder="Select campaign"
                          options={campaignOptions}
                          component={TouchAwareSelect}
                          showSearch
                          filterOption={(input, option) =>
                            option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                        />
                      </>
                    )}
                  </Col>
                  <Col span={campaignId || isEdit ? 12 : 12}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Platform *
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Target platform for this advertisement
                    </Text>
                    <Field
                      name="platform"
                      label="Platform"
                      options={PLATFORM_OPTIONS}
                      placeholder="Select platform"
                      allowClear
                      component={TouchAwareSelect}
                    />
                  </Col>
                  <Col span={24}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Header *
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Main title that will be displayed prominently in the ad
                    </Text>
                    <Field
                      required
                      name="header"
                      validate={validateRequired}
                      label="Header"
                      placeholder="Enter ad header"
                      component={TextInput}
                    />
                  </Col>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Call-to-Action Text *
                    </Text>
                    <Field
                      required
                      name="cta_text"
                      label="CTA Text"
                      placeholder="e.g., Shop Now, Learn More"
                      component={TextInput}
                    />
                  </Col>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Secondary Call-to-Action Text{isKarmaSource(values) && ' *'}
                    </Text>
                    <Field
                      required={isKarmaSource(values)}
                      name="secondary_cta_text"
                      label="Secondary CTA Text"
                      placeholder="e.g., See Details, Dismiss"
                      component={TextInput}
                    />
                  </Col>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Third Call-to-Action Text{isKarmaSource(values) && isMobilePlatform(values) ? ' *' : ''}
                    </Text>
                    <Field
                      required={isKarmaSource(values) && isMobilePlatform(values)}
                      name="third_cta_text"
                      label="Third CTA Text"
                      placeholder="e.g., Learn More, Get Started"
                      component={TextInput}
                    />
                  </Col>
                  <Col span={24}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Full Description
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Detailed description of the product or service being advertised
                    </Text>
                    <FullWidthFieldWrapper>
                      <Field
                        name="description"
                        placeholder="Enter full description"
                        rows={3}
                        component={TextArea}
                      />
                    </FullWidthFieldWrapper>
                  </Col>
                </Row>
              </FormSection>
              <Divider />
              <FormSection>
                <Title level={4}>Status & Schedule</Title>
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Status
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Current status of the ad campaign
                    </Text>
                    <Field
                      name="status"
                      label="Status"
                      options={STATUS_OPTIONS}
                      placeholder="Select status"
                      component={Select}
                    />
                  </Col>
                  <Col span={8}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Start Date
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      When the ad campaign should start
                    </Text>
                    <Field
                      name="started_at"
                      label="Started At"
                      placeholder="Select start date"
                      parse={(value) => value ? value.toISOString() : null}
                      format={(value) => value ? moment(value) : null}
                      showTime
                      component={DatePicker}
                    />
                  </Col>
                  <Col span={8}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      End Date
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      When the ad campaign should end
                    </Text>
                    <Field
                      name="end_at"
                      label="End At"
                      placeholder="Select end date"
                      parse={(value) => value ? value.toISOString() : null}
                      format={(value) => value ? moment(value) : null}
                      showTime
                      component={DatePicker}
                    />
                  </Col>
                </Row>
              </FormSection>

              <Divider />

              <FormSection>
                <Title level={4}>URLs</Title>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Terms & Conditions URL
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Link to terms and conditions page (optional)
                    </Text>
                    <Field
                      name="terms_url"
                      label="Terms URL"
                      validate={validateOptionalURL}
                      placeholder="https://example.com/terms"
                      component={TextInput}
                    />
                  </Col>


                  <Col span={24}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Click Destination URL *
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Primary destination where users will be redirected when clicking the ad
                    </Text>
                    <LinkConverter
                      onUrlConverted={(url) => form.change('click_url', url)}
                    />
                    <Field
                      required
                      name="click_url"
                      validate={validateRequiredURL}
                      label="Click URL"
                      placeholder="https://example.com/product"
                      component={TextInput}
                    />
                  </Col>
                  <Col span={24}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Rule ID{isKarmaSource(values) && ' *'}
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Rule of the referral link will be shown in the transactions
                    </Text>
                    <Field
                      required={isKarmaSource(values)}
                      name="rule_id"
                      validate={isKarmaSource(values) ? validateRequired : undefined}
                      label="Rule ID"
                      placeholder="Type domain to search (min 2 chars)"
                      component={RuleSearchSelect}
                    />
                  </Col>
                </Row>
              </FormSection>

              <Divider />

              <FormSection>
                <Title level={4}>Footer Information</Title>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Footer Text
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Additional text to display at the bottom of the ad
                    </Text>
                    <Field
                      name="footer_text"
                      label="Footer Text"
                      placeholder="Enter footer text"
                      component={TextInput}
                    />
                  </Col>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Footer Policy Text
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Policy-related text for the footer
                    </Text>
                    <Field
                      name="footer_policy_text"
                      label="Footer Policy Text"
                      placeholder="Enter footer policy text"
                      component={TextInput}
                    />
                  </Col>
                  <Col span={24}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Footer Policy URL
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Link to policy page referenced in footer
                    </Text>
                    <Field
                      name="footer_policy_url"
                      label="Footer Policy URL"
                      validate={validateOptionalURL}
                      placeholder="https://example.com/policy"
                      component={TextInput}
                    />
                  </Col>
                </Row>
              </FormSection>

              <Divider />

              <FormSection>
                <Title level={4}>Targeting</Title>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Target Countries
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Select the countries where this ad should be displayed
                    </Text>
                    <Field
                      name="countries"
                      label="Countries"
                      options={countryOptions}
                      placeholder="Select target countries"
                      mode="multiple"
                      allowClear
                      showSearch
                      filterOption={(input, option) => {
                        if (!input) return true
                        const label = option?.children || option?.props?.children
                        if (!label) return true
                        return label.toLowerCase().includes(input.toLowerCase())
                      }}
                      component={Select}
                    />
                  </Col>
                  <Col span={24}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Categories
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Select the categories for this ad campaign
                    </Text>
                    <Field
                      name="categories"
                      label="Categories"
                      options={categoryOptions}
                      placeholder="Select categories"
                      mode="multiple"
                      allowClear
                      showSearch
                      filterOption={(input, option) => {
                        if (!input) return true
                        const label = option?.children || option?.props?.children
                        if (!label) return true
                        return label.toLowerCase().includes(input.toLowerCase())
                      }}
                      component={Select}
                    />
                  </Col>
                  <Col span={24}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Allowed Domains
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Enter domain where this ad is allowed to be displayed (separate with commas). For example: www.amazon.com, us.gogo.com, asos.com.
                    </Text>
                    <Field
                      name="allowed_domains"
                      label="Allowed Domains"
                      placeholder="example.com, another-site.com"
                      mode="tags"
                      allowClear
                      tokenSeparators={[',']}
                      style={{ width: '100%' }}
                      component={Select}
                    />
                  </Col>
                </Row>
              </FormSection>

              <Divider />

              <FormSection>
                <Title level={4}>Images</Title>
                <Row gutter={[24, 24]}>
                  <Col span={8}>
                    <ImageUploadSection>
                      <Title level={5}>Icon</Title>
                      {iconPreview ? (
                        <ImagePreview>
                          <Image
                            width={IMAGE_CONFIG.icon.previewSize.width}
                            height={IMAGE_CONFIG.icon.previewSize.height}
                            src={iconPreview}
                            alt="Icon preview"
                            style={{ borderRadius: 8 }}
                          />
                          <Button
                            type="primary"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={handleRemoveIcon}
                            style={{ marginTop: 8 }}
                            disabled={isUploadingIcon}
                          >
                            Remove
                          </Button>
                        </ImagePreview>
                      ) : (
                        <Upload
                          beforeUpload={handleIconUpload}
                          accept="image/*"
                          showUploadList={false}
                          disabled={isUploadingIcon}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            loading={isUploadingIcon}
                            disabled={isUploadingIcon}
                          >
                            {isUploadingIcon ? 'Uploading...' : 'Upload Icon'}
                          </Button>
                        </Upload>
                      )}
                      {/* Icon dimensions */}
                      <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
                        <Col span={12}>
                          <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            Icon Width
                          </Text>
                          <Field
                            name="icon_width"
                            label="Icon Width"
                            placeholder={IMAGE_CONFIG.icon.defaultDimensions.width.toString()}
                            component={TextInput}
                            type="number"
                          />
                        </Col>
                        <Col span={12}>
                          <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            Icon Height
                          </Text>
                          <Field
                            name="icon_height"
                            label="Icon Height"
                            placeholder={IMAGE_CONFIG.icon.defaultDimensions.height.toString()}
                            component={TextInput}
                            type="number"
                          />
                        </Col>
                      </Row>
                    </ImageUploadSection>
                  </Col>
                  <Col span={8}>
                    <ImageUploadSection>
                      <Title level={5}>Hero Image</Title>
                      {heroPreview ? (
                        <ImagePreview>
                          <Image
                            width={IMAGE_CONFIG.hero.previewSize.width}
                            height={IMAGE_CONFIG.hero.previewSize.height}
                            src={heroPreview}
                            alt="Hero preview"
                            style={{ borderRadius: 8 }}
                          />
                          <Button
                            type="primary"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={handleRemoveHero}
                            style={{ marginTop: 8 }}
                            disabled={isUploadingHero}
                          >
                            Remove
                          </Button>
                        </ImagePreview>
                      ) : (
                        <Upload
                          beforeUpload={handleHeroUpload}
                          accept="image/*"
                          showUploadList={false}
                          disabled={isUploadingHero}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            loading={isUploadingHero}
                            disabled={isUploadingHero}
                          >
                            {isUploadingHero ? 'Uploading...' : 'Upload Hero Image'}
                          </Button>
                        </Upload>
                      )}
                      {/* Hero dimensions */}
                      <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
                        <Col span={12}>
                          <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            Hero Width
                          </Text>
                          <Field
                            name="hero_width"
                            label="Hero Width"
                            placeholder={IMAGE_CONFIG.hero.defaultDimensions.width.toString()}
                            component={TextInput}
                            type="number"
                          />
                        </Col>
                        <Col span={12}>
                          <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            Hero Height
                          </Text>
                          <Field
                            name="hero_height"
                            label="Hero Height"
                            placeholder={IMAGE_CONFIG.hero.defaultDimensions.height.toString()}
                            component={TextInput}
                            type="number"
                          />
                        </Col>
                      </Row>
                    </ImageUploadSection>
                  </Col>
                  <Col span={8}>
                    <ImageUploadSection>
                      <Title level={5}>Logo Icon</Title>
                      {logoIconPreview ? (
                        <ImagePreview>
                          <Image
                            width={IMAGE_CONFIG.logoIcon.previewSize.width}
                            height={IMAGE_CONFIG.logoIcon.previewSize.height}
                            src={logoIconPreview}
                            alt="Logo icon preview"
                            style={{ borderRadius: 8 }}
                          />
                          <Button
                            type="primary"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={handleRemoveLogoIcon}
                            style={{ marginTop: 8 }}
                            disabled={isUploadingLogoIcon}
                          >
                            Remove
                          </Button>
                        </ImagePreview>
                      ) : (
                        <Upload
                          beforeUpload={handleLogoIconUpload}
                          accept="image/*"
                          showUploadList={false}
                          disabled={isUploadingLogoIcon}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            loading={isUploadingLogoIcon}
                            disabled={isUploadingLogoIcon}
                          >
                            {isUploadingLogoIcon ? 'Uploading...' : 'Upload Logo Icon'}
                          </Button>
                        </Upload>
                      )}
                      <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
                        <Col span={12}>
                          <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            Logo Icon Width
                          </Text>
                          <Field
                            name="logo_icon_width"
                            label="Logo Icon Width"
                            placeholder={IMAGE_CONFIG.logoIcon.defaultDimensions.width.toString()}
                            component={TextInput}
                            type="number"
                          />
                        </Col>
                        <Col span={12}>
                          <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            Logo Icon Height
                          </Text>
                          <Field
                            name="logo_icon_height"
                            label="Logo Icon Height"
                            placeholder={IMAGE_CONFIG.logoIcon.defaultDimensions.height.toString()}
                            component={TextInput}
                            type="number"
                          />
                        </Col>
                      </Row>
                    </ImageUploadSection>
                  </Col>
                </Row>
              </FormSection>

              <ButtonContainer>
                <Button onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isSaving}
                  disabled={hasValidationErrors}
                >
                  {isEdit ? 'Update Ad' : 'Create Ad'}
                </Button>
              </ButtonContainer>
            </StyledForm>
          )
        }}
      />
    </Spin>
  )
}

AdForm.propTypes = {
  adId: PropTypes.string,
  campaignId: PropTypes.string,
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

export default AdForm